// Demo composer that mirrors the real MessageComposer anatomy
// (border-t bg-card p-4 → action row of icon buttons + textarea + Send),
// with the Crisp-style "Aa" toggle added to the action row.
//
// Wraps the production RichTextEditor + RichToolbar — what you see here is
// what ships, just stripped of attachments / suggestions / translation
// affordances for the design page.

import { useState } from 'react'
import { Send, Smile, Type, Paperclip } from 'lucide-react'
import type { Editor } from '@tiptap/react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { RichTextEditor } from '@/components/inbox/RichTextEditor'
import { RichToolbar } from '@/components/inbox/RichToolbar'

interface RichComposerProps {
  initialHtml: string
  onSend: (html: string) => void
}

export function RichComposer({ initialHtml, onSend }: RichComposerProps) {
  const [expanded, setExpanded] = useState(false)
  const [plain, setPlain] = useState('')
  const [rich, setRich] = useState(initialHtml)
  const [editor, setEditor] = useState<Editor | null>(null)

  function send() {
    onSend(expanded ? rich : plain)
  }

  return (
    <div className="border-t border-border bg-card p-4 shrink-0">
      <div className="space-y-2">

        {expanded && (
          <div className="bg-muted rounded-lg overflow-hidden">
            <RichToolbar editor={editor} />
            <RichTextEditor value={rich} onChange={setRich} onEditorReady={setEditor} />
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send() }}
          className="flex gap-2 items-end relative"
        >
          <Button type="button" variant="ghost" size="icon" className="cursor-pointer shrink-0" title="Attach file">
            <Paperclip className="size-4" />
          </Button>

          <Button type="button" variant="ghost" size="icon" className="cursor-pointer shrink-0" title="Insert emoji">
            <Smile className="size-4" />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setExpanded(e => !e)}
            className={cn('cursor-pointer shrink-0', expanded && 'bg-primary/15 text-primary hover:bg-primary/20')}
            title={expanded ? 'Hide formatting' : 'Show formatting'}
            aria-pressed={expanded}
          >
            <Type className="size-4" />
          </Button>

          {!expanded && (
            <textarea
              value={plain}
              onChange={(e) => setPlain(e.target.value)}
              placeholder="Type your message..."
              rows={1}
              className="flex-1 bg-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[40px] max-h-[200px] overflow-y-auto"
              onInput={(e) => {
                const t = e.target as HTMLTextAreaElement
                t.style.height = 'auto'
                t.style.height = `${Math.min(t.scrollHeight, 200)}px`
              }}
            />
          )}

          {expanded && (
            <div className="flex-1 text-xs text-muted-foreground self-center pl-2 truncate">
              Formatting on — write above
            </div>
          )}

          <Button type="submit" className="cursor-pointer shrink-0">
            <Send className="size-4" />
          </Button>
        </form>
      </div>
    </div>
  )
}
